import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { motion, AnimatePresence } from 'framer-motion';
import type { AvatarCoreProps } from './types';
import { buildMPNativeClip } from '@/lib/language/nativeAvatarMotion';
import { createLanguageExpressionDriver, type ExpressionMorphTarget } from '@/lib/language/avatarExpression';
import { loadExternalAnimationClip } from './loadAnimationClip';
import { ActionStopScheduler } from './actionStopScheduler';
import { stripSpeechOwnedMorphTracks } from './speechOwnedMorphTracks';
import { useLipSync, type LipSyncMorphTarget } from '@/hooks/useLipSync';
import { useFaceBaseline } from '@/hooks/useFaceBaseline';
import { useEyeBlink } from '@/hooks/useEyeBlink';
import { useMicroexpression } from '@/hooks/useMicroexpression';
import { getGLTFFromCache, preloadGLTF } from '@/lib/avatarPreloader';
import {
  areAllMpRiggedClips,
  isMpRiggedClipDriving,
  isMpRiggedClipUrl,
} from './animationClipPolicy';

const AVATAR_CONFIG = {
  targetHeight: 2.9,
  camera: {
    full: {
      position: new THREE.Vector3(0, 1.50, 7.5),
      lookAt: new THREE.Vector3(0, 1.13, 0),
    },
    bust: {
      position: new THREE.Vector3(0, 1.2, 2.4),
      lookAt: new THREE.Vector3(0, 0.8, 0),
    },
  },
};

export default function AvatarCore({
  modelUrl,
  height,
  enableRotate = true,
  framing = 'full',
  isAvaturnModel = false,
  analyserNode,
  lookAtYOverride,
  scaleOverride,
  cameraDistanceOverride,
  modelTiltXRadians,
  animationUrl,
  onReady,
  onLipSyncTargetReady,
  audioElement,
  alignment,
  mirrorState,
  stateAnimations,
  nativeMotion,
  languageExpression,
}: AvatarCoreProps) {
  const mountRef = useRef<HTMLDivElement>(null);
  const mixerRef = useRef<THREE.AnimationMixer | null>(null);
  const currentActionRef = useRef<THREE.AnimationAction | null>(null);
  const currentAnimationUrlRef = useRef<string | null>(null);
  const animationTargetRef = useRef<THREE.Object3D | null>(null);
  const clockRef = useRef(new THREE.Clock());
  const nativeMotionEnabled = nativeMotion !== undefined;
  const languageExpressionEnabled = languageExpression !== undefined;
  const languageExpressionRef = useRef(languageExpression);
  languageExpressionRef.current = languageExpression;
  const expressionDriverRef = useRef<ReturnType<typeof createLanguageExpressionDriver> | null>(null);
  const nativeMotionRef = useRef(nativeMotion);
  nativeMotionRef.current = nativeMotion;
  const nativeEmbeddedClipRef = useRef<THREE.AnimationClip | undefined>();
  const nativeActionsRef = useRef<Map<string, THREE.AnimationAction>>(new Map());
  const actionStopsRef = useRef(new ActionStopScheduler<THREE.AnimationAction>());

  // Explicit GLB state actions only. No implicit external asset selection.
  const stateActionsRef = useRef<Map<string, THREE.AnimationAction>>(new Map());
  const stateAnimationsLoadedRef = useRef<boolean>(false);
  const [modelReady, setModelReady] = useState(false);
  // Group B race fix — refs (mixerRef, animationTargetRef) are populated
  // inside the async setupModel inside the main useEffect. Effect 1 (Group B
  // pre-load) runs eagerly when `stateAnimations` is provided, but at that
  // moment those refs may still be null (setupModel hasn't finished). React
  // doesn't observe ref mutations, so the effect would never re-run. This
  // state acts as a re-render trigger: setMixerReady(true) after mixer
  // creation flips the dep, Effect 1 re-runs, refs are now ready.
  const [mixerReady, setMixerReady] = useState(false);

  // Lip sync refs
  const jawBoneRef = useRef<THREE.Bone | null>(null);
  const morphMeshRef = useRef<THREE.Mesh | null>(null);
  const morphIndexRef = useRef<number>(-1);
  const analyserNodeRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);

  // Spine bone for forward-tilt eye-contact correction (mobile Mirror).
  // Detected during setupModel via a name-fallback list (skeletons differ
  // across providers/exports). When `modelTiltXRadians` prop is defined,
  // animate() re-applies the rotation.x every frame to defend against
  // mixer.update() overwrites — same pattern as jawBoneRef lip sync.
  const spineBoneRef = useRef<THREE.Bone | null>(null);

  // Fase 4 — ARKit52 head mesh target for useLipSync. State (not ref) so
  // useLipSync's effect re-attaches when the GLB finishes loading.
  const [lipSyncMeshTarget, setLipSyncMeshTarget] = useState<LipSyncMorphTarget | null>(null);

  useLipSync({
    audioElement: audioElement ?? null,
    alignment: alignment ?? null,
    morphTarget: lipSyncMeshTarget,
    // Mini-TTS has no alignment: useLipSync decodes its audio envelope.
    enabled: !!audioElement && !!lipSyncMeshTarget,
  });

  // Liveness Group A — speaking state derived from the same audio element
  // useLipSync watches. Drives `useFaceBaseline` so the baseline smile fades
  // out while TTS plays (preventing race on mouthSmileLeft/Right with the
  // viseme path). useEyeBlink + useMicroexpression are always-on; they
  // target morphs (eyeBlink*, brow*, eyeSquint*, cheekPuff, noseSneer*) that
  // useLipSync never writes, so they're race-free regardless of speech state.
  const [isSpeaking, setIsSpeaking] = useState(false);
  useEffect(() => {
    if (!audioElement) {
      setIsSpeaking(false);
      return;
    }
    const onPlay = () => setIsSpeaking(true);
    const onPauseOrEnd = () => setIsSpeaking(false);
    audioElement.addEventListener('play', onPlay);
    audioElement.addEventListener('pause', onPauseOrEnd);
    audioElement.addEventListener('ended', onPauseOrEnd);
    setIsSpeaking(!audioElement.paused && !audioElement.ended);
    return () => {
      audioElement.removeEventListener('play', onPlay);
      audioElement.removeEventListener('pause', onPauseOrEnd);
      audioElement.removeEventListener('ended', onPauseOrEnd);
    };
  }, [audioElement]);

  useFaceBaseline({
    morphTarget: lipSyncMeshTarget,
    isSpeaking,
    enabled: !!lipSyncMeshTarget && !languageExpressionEnabled,
    smileWeight: 0.08,
  });

  useEyeBlink({
    morphTarget: lipSyncMeshTarget,
    enabled: !!lipSyncMeshTarget && !languageExpressionEnabled,
  });

  useMicroexpression({
    morphTarget: lipSyncMeshTarget,
    enabled: !!lipSyncMeshTarget && !languageExpressionEnabled,
  });

  // Refs mirror props/state so the animate() closure (locked to the main
  // setupModel useEffect deps) can read fresh values each frame without
  // forcing a model reload when audioElement/alignment/lipSyncMeshTarget
  // changes. Same pattern as analyserNodeRef above.
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const lipSyncMeshTargetRef = useRef<LipSyncMorphTarget | null>(null);
  useEffect(() => {
    audioElementRef.current = audioElement ?? null;
  }, [audioElement]);
  useEffect(() => {
    lipSyncMeshTargetRef.current = lipSyncMeshTarget;
  }, [lipSyncMeshTarget]);

  const onReadyRef = useRef<(() => void) | undefined>(onReady);
  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  // LOTE 4.1 (05/09/2026, autorização explícita do Rafa — SÓ este prop):
  // publica lipSyncMeshTarget para o consumidor. Um efeito sobre o estado
  // cobre TODOS os setLipSyncMeshTarget (mesh principal, richer-mesh override
  // e o null do cleanup) sem tocar nos call sites; o cleanup do efeito entrega
  // null também no unmount. Ref espelhada, como onReadyRef: trocar o callback
  // não recarrega nada. Sem o prop, nada aqui tem efeito.
  const onLipSyncTargetReadyRef = useRef<AvatarCoreProps['onLipSyncTargetReady']>(onLipSyncTargetReady);
  useEffect(() => {
    onLipSyncTargetReadyRef.current = onLipSyncTargetReady;
  }, [onLipSyncTargetReady]);
  useEffect(() => {
    onLipSyncTargetReadyRef.current?.(lipSyncMeshTarget);
    return () => {
      onLipSyncTargetReadyRef.current?.(null);
    };
  }, [lipSyncMeshTarget]);

  // Known MetaPerson paths require explicit MP GLB clips; nativeMotion blocks all externals.
  const isMpCatalogPath = /\/prerendered\/(male|female)-\d+\.glb(\?|$)/i.test(modelUrl);
  const isMpGeneratedPath = /\/generated\/[^/]+\/[^/?]+\.glb(\?|$)/i.test(modelUrl);

  const animationUrlIsRegisteredMpClip = isMpRiggedClipUrl(animationUrl);
  const stateClipsAllMpRigged = areAllMpRiggedClips(stateAnimations);

  // Espelho em ref do predicado de conjunto, pelo MESMO motivo do bloco de
  // refs de :174-189: o closure do animate() está preso às deps do effect
  // principal, que não incluem `stateAnimations`. Sem o espelho, o loop leria
  // o valor do render em que o effect rodou. Só o tilt do Spine consome.
  const stateClipsAllMpRiggedRef = useRef(false);
  useEffect(() => {
    stateClipsAllMpRiggedRef.current = stateClipsAllMpRigged;
  }, [stateClipsAllMpRigged]);

  useEffect(() => {
    analyserNodeRef.current = analyserNode ?? null;
    if (analyserNode) {
      dataArrayRef.current = new Uint8Array(analyserNode.frequencyBinCount);
    } else {
      dataArrayRef.current = null;
    }
  }, [analyserNode]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const actionStops = actionStopsRef.current;
    const nativeActions = nativeActionsRef.current;

    const w = mount.clientWidth || 400;
    const h = mount.clientHeight || 600;
    let cancelled = false;

    // ── Scene ──
    const scene = new THREE.Scene();
    scene.background = null;

    // ── Camera ──
    const camera = new THREE.PerspectiveCamera(36, w / h, 0.1, 100);

    // ── Renderer ──
    // iOS 18.x Mobile Safari regression: gl.getShaderPrecisionFormat(VERTEX_SHADER, HIGH_FLOAT)
    // returns null instead of an object, causing Three.js WebGLCapabilities to throw inside the
    // WebGLRenderer constructor. Detect iOS Safari and force mediump to skip the highp query path.
    // Quality difference on phone display is imperceptible for character rendering.
    const isIOSMobileSafari = typeof navigator !== 'undefined' &&
      /iPad|iPhone|iPod/.test(navigator.userAgent) &&
      /Safari/.test(navigator.userAgent) &&
      !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
        powerPreference: 'default',
        precision: isIOSMobileSafari ? 'mediump' : 'highp',
      });
    } catch (err) {
      // Last-resort retry with the most conservative WebGL config — covers
      // future iOS regressions or other devices where highp init throws.
      console.error('[AvatarCore] WebGLRenderer init threw, retrying with mediump:', err);
      renderer = new THREE.WebGLRenderer({
        antialias: false,
        alpha: true,
        powerPreference: 'default',
        precision: 'mediump',
      });
    }
    console.info('[AvatarCore:DIAG] renderer init', {
      precision: isIOSMobileSafari ? 'mediump' : 'highp',
      ua: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 80) : 'unknown',
    });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 0.50;
    renderer.shadowMap.enabled = true;
    renderer.setClearColor(0x000000, 0);
    renderer.domElement.style.cursor = enableRotate ? 'grab' : 'default';

    // ── PBR environment (iOS Safari fix) ──
    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    pmremGenerator.compileEquirectangularShader();
    const envTexture = pmremGenerator.fromScene(new RoomEnvironment()).texture;
    scene.environment = envTexture;
    pmremGenerator.dispose();

    // ── Lighting ──
    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const key = new THREE.DirectionalLight(0xffffff, 1.4);
    key.position.set(1.5, 3, 2.5);
    key.castShadow = true;
    scene.add(key);
    const fill = new THREE.DirectionalLight(0x88bbff, 0.35);
    fill.position.set(-2, 1.5, 1);
    scene.add(fill);
    const rim = new THREE.DirectionalLight(0x00e5ff, 0.25);
    rim.position.set(0, 2, -3);
    scene.add(rim);

    // Branch: Avaturn path uses scale/camera math from AvatarCard.
    // MetaPerson path uses AVATAR_CONFIG presets from AvatarViewer.
    const camPreset = AVATAR_CONFIG.camera[framing];
    const effectiveCameraZ = cameraDistanceOverride ?? camPreset.position.z;
    const mpCameraLookY =
      lookAtYOverride !== undefined ? lookAtYOverride : camPreset.lookAt.y;

    // Avatar pivot (MetaPerson path uses a pivot for orbit; Avaturn path
    // attaches model directly so OrbitControls handles camera movement).
    let avatarRoot: THREE.Group | null = null;
    let controls: OrbitControls | null = null;

    // Mount canvas immediately so controls can bind; skeleton overlay covers
    // the empty scene until the model is ready.
    mount.appendChild(renderer.domElement);

    // ── GLB loader with avatarPreloader cache ──
    // Cache HIT: SkeletonUtils.clone the cached scene (~10-30ms, no fetch/decode/DRACO worker spawn).
    // Cache MISS: preloadGLTF fetches/decodes/populates _cache for next mount.
    // preloadGLTF has in-flight Promise de-dup, so concurrent mounts share one fetch.
    const loadGLB = async (): Promise<{
      scene: THREE.Group;
      animations: THREE.AnimationClip[];
    }> => {
      const SkeletonUtilsModule = await import('three/examples/jsm/utils/SkeletonUtils.js');
      const cloneFn = SkeletonUtilsModule.clone || (SkeletonUtilsModule as any).SkeletonUtils?.clone;

      const cached = getGLTFFromCache(modelUrl);
      if (cached) {
        const scene = (cloneFn ? cloneFn(cached.scene) : cached.scene.clone(true)) as THREE.Group;
        return { scene, animations: cached.animations || [] };
      }

      const gltf = await preloadGLTF(modelUrl);
      const scene = (cloneFn ? cloneFn(gltf.scene) : gltf.scene.clone(true)) as THREE.Group;
      return { scene, animations: gltf.animations || [] };
    };

    const setupModel = async () => {
      try {
        const { scene: model, animations: embeddedAnimations } = await loadGLB();
        if (cancelled) return;

        // CRITICAL T-pose fix: hidden until all transforms + animation ready.
        model.visible = false;

        // Fix materials for iOS PBR
        model.traverse((child: any) => {
          if (child.isMesh) {
            child.castShadow = true;
            child.receiveShadow = true;
            const mats = Array.isArray(child.material) ? child.material : [child.material];
            mats.forEach((mat: any) => {
              if (!mat) return;
              mat.needsUpdate = true;
              if (mat.map) {
                mat.map.needsUpdate = true;
                mat.map.flipY = false;
              }
              if (mat.normalMap) mat.normalMap.needsUpdate = true;
              if (mat.roughnessMap) mat.roughnessMap.needsUpdate = true;
              if (mat.metalnessMap) mat.metalnessMap.needsUpdate = true;
            });
          }
        });

        // Avaturn GLBs are Z-up — rotate to Y-up BEFORE measuring.
        if (isAvaturnModel) {
          // Detect GLB orientation empirically from pre-rotation bounding box.
          // Avaturn T2 exports Y-up natively (sizeY > sizeZ). Older T1 or other
          // providers may export Z-up (sizeZ > sizeY) and need explicit rotation.
          // Without this gate, Y-up GLBs get double-rotated when combined with
          // Avaturn-native Mixamo animations also baked in Y-up.
          const orientationBox = new THREE.Box3().setFromObject(model);
          const orientationSize = orientationBox.getSize(new THREE.Vector3());
          const isZUpGLB = orientationSize.z > orientationSize.y;

          if (isZUpGLB) {
            model.rotation.set(-Math.PI / 2, 0, 0);
            model.updateMatrixWorld(true);
            console.log('[GLB_ORIENTATION_FIX] Z-up GLB detected, rotation applied');
          } else {
            console.log('[GLB_ORIENTATION_FIX] Y-up GLB detected, rotation skipped');
          }
        }

        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());

        console.log('[avatar:DIAG] initial measurement', {
          isAvaturnModel,
          modelUrl: modelUrl.substring(modelUrl.lastIndexOf('/') + 1),
          framing,
          scaleOverridePassed: scaleOverride,
          lookAtYOverridePassed: lookAtYOverride,
          cameraDistanceOverridePassed: cameraDistanceOverride,
          boundingBoxSize: { x: size.x.toFixed(3), y: size.y.toFixed(3), z: size.z.toFixed(3) },
          boundingBoxCenter: { x: center.x.toFixed(3), y: center.y.toFixed(3), z: center.z.toFixed(3) },
        });

        if (isAvaturnModel) {
          // Avaturn path (matches AvatarCard lines 97-108).
          model.position.x -= center.x;
          model.position.z -= center.z;
          model.position.y -= box.min.y;

          const scaleFactor = scaleOverride ?? 1.5;
          model.scale.setScalar((1.75 / size.y) * scaleFactor);
          const avatarHeight = 1.75 * scaleFactor;

          const lookAtY =
            lookAtYOverride !== undefined ? lookAtYOverride : avatarHeight * 0.15;
          const orbitRadius = cameraDistanceOverride ?? 4.0;
          // Tilt minimal (phi=1.5 rad, ~86°): camera nearly horizontal with subtle 3D depth.
          // Was phi=1.25 (top-down compensation for buggy avatar posture); now avatar has correct
          // posture so heavy top-down angle works against it (confirmed insight from Rafa 28/04).
          camera.position.set(0, lookAtY + orbitRadius * 0.0707, orbitRadius * 0.9975);
          camera.lookAt(0, lookAtY, 0);

          scene.add(model);
          avatarRoot = null;
          animationTargetRef.current = model;

          if (enableRotate) {
            model.updateMatrixWorld(true);
            const finalBox = new THREE.Box3().setFromObject(model);
            const avatarCenter = finalBox.getCenter(new THREE.Vector3());
            controls = new OrbitControls(camera, renderer.domElement);
            const targetY = lookAtYOverride !== undefined ? lookAtYOverride : avatarCenter.y;
            controls.target.set(avatarCenter.x, targetY, avatarCenter.z);
            controls.enableDamping = true;
            controls.enablePan = false;
            controls.update();
          }
        } else {
          // MetaPerson path (matches AvatarViewer).
          const modelHeight = Math.max(size.x, size.y, size.z);
          const computedScale = AVATAR_CONFIG.targetHeight / modelHeight;
          const finalScale = computedScale * (scaleOverride ?? 1);

          console.log('[avatar:DIAG] MetaPerson scale computation', {
            targetHeight: AVATAR_CONFIG.targetHeight,
            modelHeightUsed: modelHeight.toFixed(3),
            computedScale: computedScale.toFixed(4),
            scaleOverrideMultiplier: scaleOverride ?? 1,
            finalScale: finalScale.toFixed(4),
          });

          model.position.x -= center.x;
          model.position.z -= center.z;
          model.position.y -= box.min.y;

          model.scale.setScalar(finalScale);

          model.updateMatrixWorld(true);
          const finalBox = new THREE.Box3().setFromObject(model);
          const finalCenter = finalBox.getCenter(new THREE.Vector3());
          model.position.x -= finalCenter.x;
          model.position.z -= finalCenter.z;

          console.log('[avatar:DIAG] MetaPerson final positioning', {
            finalBoxSize: {
              x: (finalBox.max.x - finalBox.min.x).toFixed(3),
              y: (finalBox.max.y - finalBox.min.y).toFixed(3),
              z: (finalBox.max.z - finalBox.min.z).toFixed(3),
            },
            finalCenterBeforeAdjust: {
              x: finalCenter.x.toFixed(3),
              y: finalCenter.y.toFixed(3),
              z: finalCenter.z.toFixed(3),
            },
            modelPositionAfter: {
              x: model.position.x.toFixed(3),
              y: model.position.y.toFixed(3),
              z: model.position.z.toFixed(3),
            },
            modelScaleApplied: model.scale.x.toFixed(4),
          });

          // Match Avaturn pattern: add model directly to scene so OrbitControls
          // orbits the model itself, not a wrapper pivot. The model is already
          // centered at origin after the post-scale re-center above. Wrapping
          // in a pivot group caused OrbitControls to rotate around the pivot
          // while the model had residual Z offset, producing a wide sweep
          // instead of axis-centered rotation.
          scene.add(model);
          avatarRoot = null;
          animationTargetRef.current = model;

          camera.position.set(0, camPreset.position.y, effectiveCameraZ);
          camera.lookAt(0, mpCameraLookY, 0);

          console.log('[avatar:DIAG] MetaPerson camera setup', {
            camPresetUsed: framing,
            cameraPosition: {
              x: 0,
              y: camPreset.position.y.toFixed(3),
              z: effectiveCameraZ.toFixed(3),
            },
            cameraLookAt: { x: 0, y: mpCameraLookY.toFixed(3), z: 0 },
            cameraFOV: camera.fov,
            controlsTargetY: enableRotate ? (lookAtYOverride ?? 'avatarCenter.y') : 'disabled',
          });

          if (enableRotate) {
            model.updateMatrixWorld(true);
            const finalAvatarBox = new THREE.Box3().setFromObject(model);
            const avatarCenter = finalAvatarBox.getCenter(new THREE.Vector3());
            controls = new OrbitControls(camera, renderer.domElement);
            const targetY = lookAtYOverride !== undefined ? lookAtYOverride : avatarCenter.y;
            controls.target.set(avatarCenter.x, targetY, avatarCenter.z);
            controls.enableDamping = true;
            controls.enablePan = false;
            controls.update();
            console.log('[avatar:DIAG] MetaPerson controls target final', {
              avatarCenterX: avatarCenter.x.toFixed(4),
              avatarCenterY: avatarCenter.y.toFixed(4),
              avatarCenterZ: avatarCenter.z.toFixed(4),
              targetYUsed: targetY.toFixed(4),
              lookAtYOverride: lookAtYOverride !== undefined ? lookAtYOverride.toFixed(4) : 'undefined',
            });
          }
        }

        // MetaPerson Body Shape preset — M1 athletic male.
        // GLB exports with all 14 body morph weights at 0 (template-base androgynous).
        // We apply masculine preset weights here so /mirror visually matches editor MP
        // intent for catalog male slots. Indices match MetaPerson export order:
        // [0]Chest+ [1]Chest- [2]Forearms+ [3]Forearms- [4]General+ [5]General-
        // [6]Hips+ [7]Hips- [8]Legs+ [9]Legs- [10]Shoulders+ [11]Shoulders-
        // [12]Waist+ [13]Waist-
        if (!isAvaturnModel) {
          model.traverse((child) => {
            const skinned = child as THREE.SkinnedMesh;
            if (skinned.isSkinnedMesh && skinned.name === 'AvatarBody' && skinned.morphTargetInfluences) {
              const preset = {
                0: 0.8,   // Chest+
                2: 0.4,   // Forearms+
                7: 0.5,   // Hips-
                10: 0.9,  // Shoulders+
                13: 0.5,  // Waist-
              };
              Object.entries(preset).forEach(([idx, w]) => {
                skinned.morphTargetInfluences![Number(idx)] = w;
              });
              console.log('[avatar:DIAG] MP Body Shape preset M1-athletic applied to AvatarBody', { preset });
            }
          });
        }

        // Mixer
        if (!mixerRef.current) {
          mixerRef.current = new THREE.AnimationMixer(model);
        }
        setMixerReady(true);

        // Lip sync target discovery
        // Fase 4 — independent of jawBone/morph legacy detection. Avaturn T2
        // exports a "Head_Mesh" with all 52 ARKit blendshapes. Fallback:
        // first mesh that carries any morph targets (T1 / other providers).
        let headMeshExact: THREE.Mesh | null = null;
        let headMeshFallback: THREE.Mesh | null = null;

        // Bug A — Avaturn export naming variance (avatars created >=03/05).
        // Enumerate every mesh that carries morph targets up front so that,
        // when the legacy exact + jawOpen paths return nothing, we can fall
        // back to a heuristic (>=30 morphs containing ARKit visemes) and
        // still log the full mesh inventory for diagnostics. The newer
        // Avaturn export ships the face as a plain Mesh (not SkinnedMesh),
        // so we cannot filter on isSkinnedMesh here.
        const ARKIT_VISEMES = [
          'jawOpen', 'mouthSmileLeft', 'mouthSmileRight',
          'mouthOpen', 'eyeBlinkLeft', 'eyeBlinkRight',
          'mouthFunnel', 'mouthPucker',
        ];
        const allMeshesWithMorphs: Array<{
          name: string;
          isSkinnedMesh: boolean;
          isMesh: boolean;
          nodeType: string;
          morphCount: number;
          morphSample: string[];
          hasArkitVisemes: boolean;
        }> = [];
        model.traverse((node) => {
          if ((node as any).morphTargetDictionary) {
            const dictKeys = Object.keys(
              (node as any).morphTargetDictionary as Record<string, number>
            );
            const arkitMatches = ARKIT_VISEMES.filter((v) =>
              dictKeys.some((k) => k.toLowerCase().includes(v.toLowerCase()))
            );
            allMeshesWithMorphs.push({
              name: (node as THREE.Object3D).name,
              isSkinnedMesh: !!(node as any).isSkinnedMesh,
              isMesh: !!(node as any).isMesh,
              nodeType: node.constructor.name,
              morphCount: dictKeys.length,
              morphSample: dictKeys.slice(0, 20),
              hasArkitVisemes: arkitMatches.length >= 3,
            });
          }
        });
        console.info('[lipsync-debug] mesh enumeration', {
          totalMeshesWithMorphs: allMeshesWithMorphs.length,
          meshes: allMeshesWithMorphs,
        });

        // Skeletons differ across providers/exports; match the FIRST bone whose
        // name appears in this fallback list. Generic "Spine" + Mixamo prefix
        // + Avaturn trailing tokens covered.
        const SPINE_NAMES = new Set([
          'Spine',
          'spine',
          'Spine_M',
          'mixamorig:Spine',
          'Spine1',
        ]);

        model.traverse((child) => {
          if (
            child instanceof THREE.Bone &&
            child.name.toLowerCase().includes('jaw') &&
            !jawBoneRef.current
          ) {
            jawBoneRef.current = child;
          }
          if (
            child instanceof THREE.Bone &&
            !spineBoneRef.current &&
            SPINE_NAMES.has(child.name)
          ) {
            spineBoneRef.current = child;
          }
          if (
            !jawBoneRef.current &&
            (child as any).isMesh &&
            (child as any).morphTargetDictionary
          ) {
            const mesh = child as THREE.Mesh;
            const dict = mesh.morphTargetDictionary as Record<string, number>;
            const idx = dict['mouthOpen'] ?? dict['jawOpen'] ?? -1;
            if (idx >= 0) {
              morphMeshRef.current = mesh;
              morphIndexRef.current = idx;
            }
          }

          // ARKit52 lip sync mesh detection — Fase 4. Runs alongside the
          // legacy jawBone/morphMesh detection above without affecting it.
          // Priority 1: exact name "Head_Mesh" (Avaturn T2 export).
          // Priority 2: any mesh whose morph dictionary contains "jawOpen"
          //            (universal ARKit signal — works for T2 head meshes
          //            with non-canonical names AND any ARKit-rigged GLB
          //            from other providers).
          if (
            (child as any).isMesh &&
            (child as any).morphTargetDictionary
          ) {
            const dict = (child as any).morphTargetDictionary as Record<string, number>;
            const m = child as THREE.Mesh;
            // Accept both Avaturn ("Head_Mesh") and MetaPerson ("AvatarHead")
            // canonical names. Heuristic + fallback paths below cover any
            // non-canonical export naming.
            const HEAD_MESH_NAMES = new Set(['Head_Mesh', 'AvatarHead']);
            if (HEAD_MESH_NAMES.has(child.name) && !headMeshExact) {
              headMeshExact = m;
            } else if (!headMeshFallback && 'jawOpen' in dict) {
              headMeshFallback = m;
            }
          }
        });

        // Spine forward-tilt: apply initial rotation once skeleton is captured.
        // animate() re-applies every frame to defend against mixer.update.
        if (!nativeMotionEnabled && modelTiltXRadians !== undefined) {
          if (spineBoneRef.current) {
            spineBoneRef.current.rotation.x = modelTiltXRadians;
          } else {
            console.warn(
              '[AvatarCore] No Spine bone found for tilt — skeleton naming may differ. Tilt prop ignored.'
            );
          }
        }

        let lipSyncMesh = (headMeshExact ?? headMeshFallback) as THREE.Mesh | null;
        const foundExact = !!headMeshExact;
        const foundFallback = !headMeshExact && !!headMeshFallback;
        let foundHeuristic = false;

        // Bug A heuristic: Avaturn renamed "Head_Mesh" and dropped the
        // jawOpen morph key on exports >=03/05, so neither legacy path
        // matches. Pick any mesh (Skinned or plain) with >=30 morphs
        // that includes ARKit viseme keys — morphTargetInfluences /
        // morphTargetDictionary are inherited from Object3D so the
        // downstream lip sync hook works on plain Mesh too.
        if (!lipSyncMesh) {
          const candidate = allMeshesWithMorphs.find(
            (m) => m.morphCount >= 30 && m.hasArkitVisemes
          );
          if (candidate) {
            const obj = model.getObjectByName(candidate.name);
            if (obj && (obj as any).morphTargetDictionary) {
              lipSyncMesh = obj as unknown as THREE.Mesh;
              foundHeuristic = true;
              console.info('[lipsync-debug] fallback heuristic matched', {
                meshName: candidate.name,
                morphCount: candidate.morphCount,
                arkitVisemesFound: ARKIT_VISEMES.filter((v) =>
                  Object.keys(
                    (lipSyncMesh as THREE.Mesh).morphTargetDictionary || {}
                  ).some((k) => k.toLowerCase().includes(v.toLowerCase()))
                ),
              });
            }
          }
        }

        // Defensive: if we got a fallback that has fewer than 100 morphs but
        // there's a candidate mesh with more morphs that also has ARKit
        // visemes, prefer the richer mesh. Protects against teeth/eyelash
        // meshes being picked over AvatarHead.
        if (lipSyncMesh && !foundExact && !foundHeuristic) {
          const currentCount = Object.keys(
            lipSyncMesh.morphTargetDictionary || {}
          ).length;
          if (currentCount < 100) {
            const richer = allMeshesWithMorphs
              .filter((mm) => mm.morphCount >= 100 && mm.hasArkitVisemes)
              .sort((a, b) => b.morphCount - a.morphCount)[0];
            if (richer) {
              const obj = model.getObjectByName(richer.name);
              if (obj && (obj as any).morphTargetDictionary) {
                const previousName = lipSyncMesh.name;
                lipSyncMesh = obj as unknown as THREE.Mesh;
                console.info('[lipsync-debug] richer-mesh override', {
                  previous: previousName,
                  chose: richer.name,
                  morphCount: richer.morphCount,
                });
                setLipSyncMeshTarget({
                  morphTargetDictionary:
                    lipSyncMesh.morphTargetDictionary as Record<string, number>,
                  morphTargetInfluences:
                    lipSyncMesh.morphTargetInfluences as unknown as number[],
                  name: lipSyncMesh.name,
                });
              }
            }
          }
        }

        if (
          !cancelled &&
          lipSyncMesh &&
          lipSyncMesh.morphTargetDictionary &&
          lipSyncMesh.morphTargetInfluences
        ) {
          setLipSyncMeshTarget({
            morphTargetDictionary: lipSyncMesh.morphTargetDictionary as Record<string, number>,
            morphTargetInfluences: lipSyncMesh.morphTargetInfluences as unknown as number[],
            name: lipSyncMesh.name,
          });
        }

        if (languageExpressionEnabled) {
          const facialMeshes: ExpressionMorphTarget[] = [];
          model.traverse(object => {
            const mesh = object as THREE.Mesh;
            if (mesh.morphTargetDictionary && mesh.morphTargetInfluences) {
              facialMeshes.push({ morphTargetDictionary: mesh.morphTargetDictionary,
                morphTargetInfluences: mesh.morphTargetInfluences });
            }
          });
          expressionDriverRef.current = createLanguageExpressionDriver(facialMeshes, modelUrl, performance.now() / 1000);
        }

        console.info('[lipsync-debug] mesh detected', {
          hasMesh: !!lipSyncMesh,
          meshName: lipSyncMesh?.name ?? null,
          foundExact,
          foundFallback,
          foundHeuristic,
          morphDictKeys: lipSyncMesh?.morphTargetDictionary
            ? Object.keys(lipSyncMesh.morphTargetDictionary).length
            : 0,
          morphInfluences: lipSyncMesh?.morphTargetInfluences?.length ?? 0,
        });

        // Use embedded animation if present and model is NOT Avaturn
        // (MetaPerson ships idle anim inside GLB; rig naming differs from
        // the Avaturn FBX → use the native embedded clip instead of
        // trying to retarget FBX).
        // MP M1 catalog (male-1.glb) uses embedded Male_Pose_Idle even when caller
        // passes isAvaturnModel=true. Typical case: DB still has avatar_provider='avaturn'
        // after picker swapped to male-1.glb (by design — provider stays until user
        // re-saves). Without this override, the avatar renders in static T-pose (bind
        // pose). The V3 arm/hand strip in the block below preserves the natural A-pose
        // + subtle idle arm micro-movement encoded in the MetaPerson export.
        const useEmbeddedAnim = (isMpCatalogPath || isMpGeneratedPath || !isAvaturnModel) && embeddedAnimations.length > 0;

        nativeEmbeddedClipRef.current = embeddedAnimations[0]?.clone();
        if (nativeMotionEnabled && mixerRef.current) {
          // Only the current model's own initial arm pose and authored gestures.
          // Capture before any mixer evaluation, preserving the original rest pose.
          const mode = nativeMotionRef.current?.mode ?? 'idle';
          const clip = buildMPNativeClip(model, nativeEmbeddedClipRef.current, mode);
          const action = mixerRef.current.clipAction(clip, model);
          action.setLoop(THREE.LoopRepeat, Infinity).reset().play();
          nativeActionsRef.current.set(mode, action);
          currentActionRef.current = action;
          currentAnimationUrlRef.current = 'native:' + mode;
        } else if (useEmbeddedAnim && mixerRef.current) {
          // Preserve the shared GLB source and give speech exclusive ownership
          // of its actual mouth channel, including packed glTF weight tracks.
          const clip = stripSpeechOwnedMorphTracks(embeddedAnimations[0], model);
          // Existing V3 body policy: retain the model's authored arm/hand pose.
          const isArmOrHandBone = (lower: string): boolean =>
            lower.startsWith('leftshoulder') || lower.startsWith('rightshoulder') ||
            lower.startsWith('leftarm') || lower.startsWith('rightarm') ||
            lower.startsWith('leftforearm') || lower.startsWith('rightforearm') ||
            lower.startsWith('lefthand') || lower.startsWith('righthand');
          clip.tracks = clip.tracks.filter(track => {
            const lower = track.name.toLowerCase();
            const boneTrack = /\.(quaternion|position|scale)$/.test(lower);
            return !boneTrack || isArmOrHandBone(lower);
          });
          const action = mixerRef.current.clipAction(clip, model);
          action.setLoop(THREE.LoopRepeat, Infinity);
          action.reset();
          action.play();
          currentActionRef.current = action;
          currentAnimationUrlRef.current = 'embedded:' + clip.name;
          console.info('[avatar:DIAG] using embedded animation', {
            clipName: clip.name,
            trackCount: clip.tracks.length,
          });
        } else {
          // A model without an embedded clip stays in its own rest pose.
          // External clips are explicit, validated GLB opt-ins in the effects below.
          console.info('[avatar:DIAG] no embedded animation; preserving model rest pose');
        }

        // Reveal after GPU processes transforms (2 frames).
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (cancelled) return;
            model.visible = true;
            setModelReady(true);
            onReadyRef.current?.();
          });
        });
      } catch (err) {
        console.error('AvatarCore load error:', err);
      }
    };

    setupModel();

    // ── Animation loop ──
    let animId: number;
    const animate = () => {
      animId = requestAnimationFrame(animate);

      if (controls) controls.update();

      if (mixerRef.current) mixerRef.current.update(clockRef.current.getDelta());

      // Persist the configured Spine tilt for the legacy embedded arm-only clip.
      // Same defensive pattern as jawBone lip sync — bone rotations driven by
      // animation tracks during clip transitions need to be re-applied each
      // frame to keep the eye-contact correction stable on mobile.
      //
      // ── Exceção /mp/ (3ª mudança escopada autorizada, Rafa 10/08) ─────────
      // A escrita abaixo é ABSOLUTA e roda DEPOIS do mixer.update() (:1007).
      // Em three.js `rotation` e `quaternion` do Object3D têm _onChange
      // cruzado, então escrever `rotation.x` recompõe o quaternion que o
      // mixer acabou de aplicar — não é escrita num campo espelho inerte.
      //
      // A LINHA NÃO SAI. Com o strip V3 (:874-890) o clipe EMBUTIDO do
      // catálogo não tem faixa de Spine, e é esta escrita que dá pose à
      // coluna de todo avatar ativo em produção hoje. Removê-la quebra a
      // base inteira; ela é pulada APENAS quando existe clipe externo da
      // allowlist /mp/ dirigindo o rig — o único caso em que há animação de
      // coluna para preservar. Mesma convenção de detecção das três guardas
      // (:1162, :1246, :1312): quem declara o rig é o path do clipe.
      // Predicado puro e testado em ./animationClipPolicy.ts.
      const mpRiggedClipDrivingSpine = isMpRiggedClipDriving({
        currentClipUrl: currentAnimationUrlRef.current,
        stateClipsAllMpRigged: stateClipsAllMpRiggedRef.current,
        stateClipsLoaded: stateAnimationsLoadedRef.current,
      });
      if (
        spineBoneRef.current &&
        modelTiltXRadians !== undefined &&
        !mpRiggedClipDrivingSpine &&
        !nativeMotionEnabled
      ) {
        spineBoneRef.current.rotation.x = modelTiltXRadians;
      }

      // Fase 4.5 — scope the gate to the amplitude legacy block only.
      // Mixer + controls + renderer.render keep running every frame so the
      // scene stays alive during speech; useLipSync drives ARKit52 morphs
      // on its own RAF and the GPU re-paints them each frame here.
      const sampledAudioModeActive =
        !!audioElementRef.current &&
        !!lipSyncMeshTargetRef.current;

      // The hook owns both timed visemes and Mini-TTS's decoded envelope.
      // Never let the legacy loop close the mouth over either driver.
      if (!sampledAudioModeActive) {
        if (analyserNodeRef.current && dataArrayRef.current) {
          analyserNodeRef.current.getByteFrequencyData(dataArrayRef.current);
          const avg =
            dataArrayRef.current.reduce((a, b) => a + b, 0) / dataArrayRef.current.length;
          const normalized = Math.min(avg / 80, 1);
          if (jawBoneRef.current) {
            const target = normalized * 0.25;
            jawBoneRef.current.rotation.x +=
              (target - jawBoneRef.current.rotation.x) * 0.3;
          } else if (morphMeshRef.current && morphIndexRef.current >= 0) {
            const influences = morphMeshRef.current.morphTargetInfluences!;
            influences[morphIndexRef.current] +=
              (normalized - influences[morphIndexRef.current]) * 0.3;
          }
        } else {
          if (jawBoneRef.current) {
            jawBoneRef.current.rotation.x +=
              (0 - jawBoneRef.current.rotation.x) * 0.15;
          }
          if (morphMeshRef.current && morphIndexRef.current >= 0) {
            const influences = morphMeshRef.current.morphTargetInfluences!;
            influences[morphIndexRef.current] +=
              (0 - influences[morphIndexRef.current]) * 0.15;
          }
        }
      }

      // One owner for the language face, including matching eyelash morphs.
      // HTML audio is authoritative; live WebRTC releases the mouth while its
      // speaking state is active. This never writes a jaw or native viseme.
      const facialAudio = audioElementRef.current;
      const speechOwnsMouth = !!(facialAudio && !facialAudio.paused && !facialAudio.ended)
        || (!!analyserNodeRef.current && nativeMotionRef.current?.mode === 'talk');
      expressionDriverRef.current?.update(languageExpressionRef.current ?? 'idle', performance.now() / 1000, speechOwnsMouth);

      renderer.render(scene, camera);
    };
    animate();

    // ── Resize ──
    const onResize = () => {
      const nw = mount.clientWidth;
      const nh = mount.clientHeight;
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    };
    window.addEventListener('resize', onResize);

    // ── Cleanup ──
    return () => {
      cancelled = true;
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', onResize);

      if (controls) {
        controls.dispose();
        controls = null;
      }

      scene.traverse((child: any) => {
        if (child.isMesh) {
          child.geometry?.dispose();
          const mats = Array.isArray(child.material) ? child.material : [child.material];
          mats.forEach((m: any) => {
            if (!m) return;
            m.map?.dispose();
            m.normalMap?.dispose();
            m.roughnessMap?.dispose();
            m.metalnessMap?.dispose();
            m.emissiveMap?.dispose();
            m.dispose();
          });
        }
      });
      envTexture?.dispose();
      if (mixerRef.current) {
        mixerRef.current.stopAllAction();
        mixerRef.current = null;
      }
      actionStops.clear();
      nativeActions.clear();
      nativeEmbeddedClipRef.current = undefined;
      currentActionRef.current = null;
      currentAnimationUrlRef.current = null;
      animationTargetRef.current = null;
      setMixerReady(false);
      jawBoneRef.current = null;
      spineBoneRef.current = null;
      morphMeshRef.current = null;
      morphIndexRef.current = -1;
      stateActionsRef.current = new Map();
      stateAnimationsLoadedRef.current = false;
      expressionDriverRef.current?.dispose();
      expressionDriverRef.current = null;
      setLipSyncMeshTarget(null);
      avatarRoot = null;
      renderer.dispose();
      renderer.forceContextLoss();
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
    };
  }, [
    modelUrl,
    enableRotate,
    framing,
    isAvaturnModel,
    scaleOverride,
    cameraDistanceOverride,
    lookAtYOverride,
    modelTiltXRadians,
    nativeMotionEnabled,
    languageExpressionEnabled,
    isMpCatalogPath,
    isMpGeneratedPath,
  ]);

  // Change authored gestures on the existing model/mixer; never reload the GLB.
  useEffect(() => {
    const mixer = mixerRef.current;
    const target = animationTargetRef.current;
    const mode = nativeMotion?.mode;
    if (!mode || !mixer || !target) return;
    if (currentAnimationUrlRef.current === 'native:' + mode) return;
    let action = nativeActionsRef.current.get(mode);
    if (!action) {
      action = mixer.clipAction(buildMPNativeClip(target, nativeEmbeddedClipRef.current, mode), target);
      action.setLoop(THREE.LoopRepeat, Infinity);
      nativeActionsRef.current.set(mode, action);
    }
    const previousAction = currentActionRef.current;
    actionStopsRef.current.cancel(action);
    action.reset().play();
    if (previousAction && previousAction !== action) {
      previousAction.crossFadeTo(action, 0.3, false);
      actionStopsRef.current.schedule(previousAction, 350,
        () => previousAction === currentActionRef.current);
    }
    currentActionRef.current = action;
    currentAnimationUrlRef.current = 'native:' + mode;
  }, [nativeMotion?.mode, mixerReady]);

  // Explicit external GLB switching; no automatic idle or FBX fallback.
  useEffect(() => {
    if (nativeMotionEnabled || stateAnimations) return;
    // Registry membership is not proof of skeleton compatibility. No implicit clips.
    if (!animationUrlIsRegisteredMpClip) return;
    const mixer = mixerRef.current;
    const target = animationTargetRef.current;
    if (!mixer || !target || !animationUrl) return;
    if (currentAnimationUrlRef.current === animationUrl) return;
    let cancelled = false;

    loadExternalAnimationClip(animationUrl).then((newClip) => {
      if (cancelled) return;
      if (!newClip) return;
      if (!mixerRef.current) return;

      const playableClip = stripSpeechOwnedMorphTracks(newClip, target);
      playableClip.tracks = playableClip.tracks.filter(
        (track) => !track.name.toLowerCase().includes('hips.position'),
      );

      const newAction = mixerRef.current.clipAction(playableClip, target);
      newAction.setLoop(THREE.LoopRepeat, Infinity);
      actionStopsRef.current.cancel(newAction);
      newAction.reset();

      const previousAction = currentActionRef.current;
      if (previousAction && previousAction !== newAction) {
        // Smooth crossFade between previous and new animation (0.5s)
        newAction.play();
        previousAction.crossFadeTo(newAction, 0.5, false);
        // Stop the previous action after fade completes to free resources
        actionStopsRef.current.schedule(previousAction, 600,
          () => previousAction === currentActionRef.current);
      } else {
        newAction.play();
      }

      currentActionRef.current = newAction;
      currentAnimationUrlRef.current = animationUrl;

      console.log('[avatar:DIAG] animation crossfade', {
        from: previousAction ? 'previous' : 'none',
        to: animationUrl.substring(animationUrl.lastIndexOf('/') + 1),
        crossFadeApplied: !!previousAction,
      });
    }).catch((err) => {
      console.error('AvatarCore: animation switch failed', {
        url: animationUrl,
        error: err,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [nativeMotionEnabled, animationUrl, stateAnimations, isMpCatalogPath, isMpGeneratedPath, animationUrlIsRegisteredMpClip, mixerReady]);

  // Preload explicit, validated GLB state clips only.
  useEffect(() => {
    if (nativeMotionEnabled || !stateAnimations) return;
    // MP catalog + generated: o conjunto INTEIRO precisa ser da allowlist /mp/
    // (regra "todas" — ver animationClipPolicy.ts: um Mixamo no meio do
    // crossfade corromperia o esqueleto de forma intermitente).
    if (!stateClipsAllMpRigged) return;
    const mixer = mixerRef.current;
    const target = animationTargetRef.current;
    if (!mixer || !target) return;

    let cancelled = false;
    const entries = Object.entries(stateAnimations) as Array<[string, string]>;

    Promise.all(
      entries.map(([state, url]) =>
        loadExternalAnimationClip(url)
          .then((clip) => ({ state, clip }))
          .catch((err) => {
            console.error('AvatarCore: state machine clip load failed', { state, url, error: err });
            return null;
          }),
      ),
    ).then((results) => {
      if (cancelled || !mixerRef.current) return;
      const newMap = new Map<string, THREE.AnimationAction>();
      for (const r of results) {
        if (!r || !r.clip) continue;
        const clip = stripSpeechOwnedMorphTracks(r.clip, target);
        clip.tracks = clip.tracks.filter(
          (t) => !t.name.toLowerCase().includes('hips.position'),
        );
        const action = mixerRef.current.clipAction(clip, target);
        action.setLoop(THREE.LoopRepeat, Infinity);
        newMap.set(r.state, action);
      }
      stateActionsRef.current = newMap;
      stateAnimationsLoadedRef.current = true;

      // Stop the bootstrap action from setupModel and start the action that
      // matches the current mirrorState (or idle as fallback).
      const initialState = mirrorState ?? 'idle';
      const targetAction = newMap.get(initialState) ?? newMap.get('idle');
      if (!targetAction) return;
      const previousAction = currentActionRef.current;
      if (previousAction && previousAction !== targetAction) {
        previousAction.fadeOut(0.3);
      }
      actionStopsRef.current.cancel(targetAction);
      targetAction.reset().fadeIn(0.3).play();
      currentActionRef.current = targetAction;
    });

    return () => {
      cancelled = true;
    };
  }, [nativeMotionEnabled, stateAnimations, mixerReady, isMpCatalogPath, stateClipsAllMpRigged]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── GLB state machine — crossfade on mirrorState change ──
  // Reads the pre-loaded action from stateActionsRef and crossfades
  // (300ms) from the previous action. No-op if state machine hasn't
  // finished pre-loading yet — the load effect above will pick up the
  // current mirrorState when ready.
  useEffect(() => {
    if (nativeMotionEnabled || !stateAnimations || !mirrorState) return;
    // Espelha a guarda do preload acima (mesmo predicado de conjunto): se o
    // preload não rodou, não há action no Map para crossfadar — os dois gates
    // precisam decidir juntos ou o state machine dessincroniza.
    if (!stateClipsAllMpRigged) return;
    if (!stateAnimationsLoadedRef.current) return;
    const targetAction = stateActionsRef.current.get(mirrorState);
    if (!targetAction) return;
    const previousAction = currentActionRef.current;
    if (previousAction === targetAction) return;
    if (previousAction) {
      previousAction.fadeOut(0.3);
    }
    actionStopsRef.current.cancel(targetAction);
    targetAction.reset().fadeIn(0.3).play();
    currentActionRef.current = targetAction;
  }, [nativeMotionEnabled, mirrorState, stateAnimations, isMpCatalogPath, isMpGeneratedPath, stateClipsAllMpRigged]);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height,
        touchAction: 'none',
        overflow: 'hidden',
      }}
    >
      <div ref={mountRef} style={{ width: '100%', height: '100%' }} />
      <AnimatePresence>
        {!modelReady && (
          <motion.div
            key="skeleton"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7 }}
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <motion.div
              animate={{ scale: [0.88, 1.18, 0.88], opacity: [0.12, 0.0, 0.12] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                position: 'absolute',
                width: 72,
                height: 72,
                borderRadius: '50%',
                border: '1px solid hsl(187 72% 50% / 0.5)',
              }}
            />
            <motion.div
              animate={{ scale: [0.92, 1.08, 0.92], opacity: [0.55, 1, 0.55] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                position: 'absolute',
                width: 44,
                height: 44,
                borderRadius: '50%',
                border: '1.5px solid hsl(187 72% 50% / 0.9)',
                boxShadow: '0 0 16px hsl(187 72% 50% / 0.4)',
              }}
            />
            <motion.div
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: 'hsl(187 72% 60%)',
                boxShadow: '0 0 10px hsl(187 72% 50% / 0.8)',
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
